import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { buildShotUrl, getAgentBuild, startAgentChange, type AgentBuild, type AgentImage } from '../../api/agent';
import { ApiError } from '../../api/client';
import { Button, Screen } from '../../ui';
import Composer from './Composer';
import ShareBar from './ShareBar';
import './studio.css';

const POLL_MS = 4000;

/**
 * /studio/builds/:buildId — a build while it happens (the agent's own progress notes, live), and
 * what it made once it is published: the phone screenshot, the link, share, and "change something".
 */
export default function StudioBuild() {
  const { buildId = '' } = useParams();
  const navigate = useNavigate();
  const [build, setBuild] = useState<AgentBuild | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const listEnd = useRef<HTMLLIElement>(null);

  // Poll while it runs; pause while the tab is hidden (a phone in a pocket).
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function tick() {
      if (!live) return;
      if (document.visibilityState === 'visible') {
        try {
          const b = await getAgentBuild(buildId);
          if (!live) return;
          setBuild(b);
          setProblem(null);
          if (b.status === 'live' || b.status === 'failed') return;
        } catch (e) {
          if (!live) return;
          if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
            setProblem("We couldn't find that build.");
            return;
          }
          setProblem('Reconnecting…');
        }
      }
      timer = setTimeout(tick, POLL_MS);
    }
    void tick();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [buildId]);

  // The elapsed clock.
  useEffect(() => {
    if (build && (build.status === 'live' || build.status === 'failed')) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [build]);

  useEffect(() => {
    listEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [build?.progress.length]);

  async function change(message: string, images: AgentImage[]) {
    if (!build) return;
    setChanging(true);
    try {
      const { buildId: next } = await startAgentChange(build.siteId, message, images);
      navigate(`/studio/builds/${next}`);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not start. Try again in a moment.');
    } finally {
      setChanging(false);
    }
  }

  if (!build) {
    return (
      <Screen title="Starting…">
        {problem ? <p className="studio-problem" role="alert">{problem}</p> : <div className="studio-spinner" aria-label="Loading" />}
      </Screen>
    );
  }

  if (build.status === 'live' && build.url) {
    const slug = new URL(build.url).hostname.split('.')[0];
    return (
      <Screen title={build.title ?? 'Your site is live'}>
        <div className="studio-result">
          <a className="studio-phone pressable" href={build.url} target="_blank" rel="noreferrer" aria-label={`Open ${build.title ?? 'your site'}`}>
            <img src={buildShotUrl(slug, build.buildId)} alt={`${build.title ?? 'Your site'} on a phone`} />
          </a>
          {build.summary && <p className="studio-summary">{build.summary}</p>}
          <ShareBar url={build.url} title={build.title ?? 'My site'} />
        </div>
        <section className="studio-section" aria-labelledby="studio-change">
          <h2 id="studio-change" className="studio-h2">Change something</h2>
          <Composer placeholder="Make the bird red, add a leaderboard…" submitLabel="Make the change" busy={changing} onSubmit={change} />
        </section>
        {problem && <p className="studio-problem" role="alert">{problem}</p>}
      </Screen>
    );
  }

  if (build.status === 'failed') {
    return (
      <Screen title="That one didn't work">
        <p className="studio-muted">{build.error ?? 'The builder stopped before it finished.'}</p>
        {build.summary && <p className="studio-summary">{build.summary}</p>}
        <Button title="Back to the studio" onPress={() => navigate('/studio')} />
      </Screen>
    );
  }

  const elapsed = Math.max(0, Math.round((now - Date.parse(build.startedAt)) / 1000));
  const lines = build.progress.length ? build.progress : [{ id: 'start', text: 'Getting started', kind: 'step' as const }];
  return (
    <Screen title={build.kind === 'change' ? 'Making your change' : 'Building'}>
      <p className="studio-lede">
        A builder is making this now and testing it on a phone-sized screen. Most take 10–25 minutes — you can leave this page and come back.
      </p>
      <div className="studio-clock" aria-live="off">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</div>
      <ol className="studio-progress" aria-live="polite">
        {lines.map((line, i) => (
          <li
            key={line.id}
            ref={i === lines.length - 1 ? listEnd : undefined}
            className={`studio-line studio-line-${line.kind} ${i === lines.length - 1 ? 'is-current' : ''}`}
          >
            <span className="studio-dot" aria-hidden="true" />
            <span>{line.text}</span>
          </li>
        ))}
        {build.status === 'finalizing' && (
          <li className="studio-line studio-line-check is-current"><span className="studio-dot" aria-hidden="true" /><span>Publishing it</span></li>
        )}
      </ol>
      {problem && <p className="studio-muted" role="status">{problem}</p>}
    </Screen>
  );
}
